import { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import type { Payment, UiPaymentStatus } from '@models/payments';
import { PaymentProviders, UiPaymentStatuses } from '@models/payments';
import {
  formatPaymentAmount,
  formatPaymentDate,
  getCustomerProfileUrl,
  getPaymentChargerFlowInfo,
  getPaymentCustomer,
  getPaymentTypeLabel,
  getProviderDashboardPaymentUrl,
  getUiPaymentStatus,
} from '@features/payments/helpers';
import JsonTree, { type JsonSearchHighlight, type JsonTreeHandle } from './JsonTree';
import { useDebounce } from '@hooks/useDebounce';
import {
  filterJsonPathEntries,
  flattenJsonForSearch,
  parseJsonSearchQuery,
} from '@utils/jsonPayloadSearch';
import { formatDistanceToNow } from 'date-fns';

import ExternalLinkButton from '@components/ui/ExternalLinkButton';
import CapturePaymentButton from '../CapturePaymentButton';
import PaymentStatusBadge from '../PaymentStatusBadge';
import PaymentTimeline from './PaymentTimeline';
import s from './PaymentDetailDrawer.module.css';

const COPIED_RESET_MS = 1500;
const SEARCH_DEBOUNCE_MS = 200;
const MAX_MATCHES_SHOWN = 300;

type DrawerTab = 'overview' | 'raw';

type PaymentsStatusFilter = UiPaymentStatus | 'all';

type PaymentDetailDrawerProps = {
  payment: Payment | null;
  onClose: () => void;
  /** When `failed`, overview shows charger flow fields from `paymentProviderError`. */
  statusFilter?: PaymentsStatusFilter;
};

function PaymentDetailDrawer({ payment, onClose, statusFilter = 'all' }: PaymentDetailDrawerProps) {
  const [tab, setTab] = useState<DrawerTab>('overview');
  const [copiedKey, setCopiedKey] = useState<'json' | 'id' | 'providerId' | 'chargerFlowId' | null>(
    null,
  );
  const [payloadSearch, setPayloadSearch] = useState('');
  const json = useMemo(() => (payment ? JSON.stringify(payment, null, 2) : ''), [payment]);

  const debouncedPayloadSearch = useDebounce(payloadSearch, SEARCH_DEBOUNCE_MS);
  const jsonTreeRef = useRef<JsonTreeHandle | null>(null);

  useEffect(() => {
    setPayloadSearch('');
    setTab('overview');
    setCopiedKey(null);
  }, [payment?.id]);

  useEffect(() => {
    if (!payment) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [payment, onClose]);

  const { pathEntries, jsonParseFailed } = useMemo(() => {
    if (!json) return { pathEntries: [], jsonParseFailed: false };
    try {
      return {
        pathEntries: flattenJsonForSearch(JSON.parse(json)),
        jsonParseFailed: false,
      };
    } catch {
      return { pathEntries: [], jsonParseFailed: true };
    }
  }, [json]);

  const parsedSearch = useMemo(
    () => parseJsonSearchQuery(debouncedPayloadSearch),
    [debouncedPayloadSearch],
  );

  const pathMatches = useMemo(() => {
    if (parsedSearch.kind === 'empty') return [];
    return filterJsonPathEntries(pathEntries, parsedSearch);
  }, [pathEntries, parsedSearch]);

  const searchHighlight = useMemo((): JsonSearchHighlight | null => {
    if (parsedSearch.kind === 'empty') return null;
    if (parsedSearch.kind === 'free') return { mode: 'free', needle: parsedSearch.needle };
    return { mode: 'pair', key: parsedSearch.key, value: parsedSearch.value };
  }, [parsedSearch]);

  const visibleMatches = pathMatches.slice(0, MAX_MATCHES_SHOWN);
  const hasMoreMatches = pathMatches.length > MAX_MATCHES_SHOWN;

  if (!payment) return null;

  const customer = getPaymentCustomer(payment);
  const typeLabel = getPaymentTypeLabel(payment);
  const providerName = payment.provider === PaymentProviders.YUNO ? 'Yuno' : 'Primer';
  const uiStatus = getUiPaymentStatus(payment);
  const amountLabel = formatPaymentAmount(
    payment.amount,
    payment.currency,
    payment.currency.decimalDigits,
  );
  const completedDate = payment.completedAt ? new Date(payment.completedAt) : null;
  const fd = completedDate ? formatPaymentDate(completedDate) : null;
  const completedRelative = completedDate
    ? formatDistanceToNow(completedDate, { addSuffix: true })
    : null;

  const copyText = (text: string, key: 'json' | 'id' | 'providerId' | 'chargerFlowId') => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedKey(key);
        setTimeout(() => setCopiedKey((curr) => (curr === key ? null : curr)), COPIED_RESET_MS);
      })
      .catch(() => {});
  };

  const matchSummary =
    parsedSearch.kind === 'empty'
      ? ''
      : `${pathMatches.length} match${pathMatches.length === 1 ? '' : 'es'}`;

  const chargerFlow =
    statusFilter === UiPaymentStatuses.FAILED
      ? getPaymentChargerFlowInfo(payment)
      : { name: '', id: '' };

  return (
    <>
      <div className={s.scrim} onClick={onClose} />
      <aside className={s.drawer} role="dialog" aria-label="Payment details">
        <header className={`${s.headerBlock} ${s[`status-${uiStatus}`]}`}>
          <div className={s.idRow}>
            <span className={s.payPill}>Payment</span>
            <span className={s.idMono} title={payment.id}>
              {payment.id}
            </span>
            <button
              type="button"
              className={s.idCopy}
              onClick={() => copyText(payment.id, 'id')}
              aria-label="Copy payment ID">
              {copiedKey === 'id' ? (
                <CheckIcon sx={{ fontSize: 14 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </button>
            <button type="button" className={s.close} onClick={onClose} aria-label="Close">
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>

          <div className={s.amountRow}>
            <span className={s.amount}>{amountLabel}</span>
            <span className={s.currency}>{payment.currency.code}</span>
            <span className={s.statusWrap}>
              <PaymentStatusBadge status={uiStatus} />
            </span>
          </div>

          {/* <div className={s.accentBar} aria-hidden /> */}
        </header>

        <nav className={s.tabs} aria-label="Payment sections">
          <button
            type="button"
            className={`${s.tab} ${tab === 'overview' ? s.tabActive : ''}`}
            onClick={() => setTab('overview')}
            aria-pressed={tab === 'overview'}>
            Overview
          </button>
          <button
            type="button"
            className={`${s.tab} ${tab === 'raw' ? s.tabActive : ''}`}
            onClick={() => setTab('raw')}
            aria-pressed={tab === 'raw'}>
            Raw payload
          </button>
        </nav>

        {tab === 'overview' ? (
          <div className={`${s.tabPanel} ${s.tabPanelOverview}`}>
            <dl className={s.grid}>
              <dt>Type</dt>
              <dd>
                <span className={s.statusPill}>{typeLabel}</span>
              </dd>
              <dt>Customer</dt>
              <dd className={s.customerCell}>
                <div className={s.customerName}>{customer.name}</div>
                <div className={s.sub}>{customer.email}</div>
              </dd>

              <dt>Processor</dt>
              <dd className={s.inlineRow}>
                <span className={s.processorName}>{payment.processor}</span>
                {payment.providerStatus ? (
                  <>
                    <span className={s.dot}>·</span>
                    <span className={s.statusPill}>{payment.providerStatus}</span>
                  </>
                ) : null}
              </dd>

              <dt>Provider ID</dt>
              <dd className={`${s.mono} ${s.inlineRow}`}>
                <span>{payment.providerId}</span>
                {payment.providerId ? (
                  <button
                    type="button"
                    className={s.idCopy}
                    onClick={() => copyText(payment.providerId, 'providerId')}
                    aria-label="Copy provider ID">
                    {copiedKey === 'providerId' ? (
                      <CheckIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    )}
                  </button>
                ) : null}
              </dd>

              {payment.brand?.brandName ? (
                <>
                  <dt>Brand</dt>
                  <dd>{payment.brand.brandName}</dd>
                </>
              ) : null}

              {fd ? (
                <>
                  <dt>Completed</dt>
                  <dd className={s.customerCell}>
                    <div>
                      {fd.date} · <span className={s.sub}>{fd.time}</span>
                    </div>
                    {completedRelative ? <div className={s.sub}>{completedRelative}</div> : null}
                  </dd>
                </>
              ) : null}

              {statusFilter === UiPaymentStatuses.FAILED ? (
                <>
                  <dt>Charger flow</dt>
                  <dd>{chargerFlow.name || 'N/A'}</dd>
                  <dt>Charger flow ID</dt>
                  <dd className={`${s.mono} ${s.inlineRow}`}>
                    <span>{chargerFlow.id || 'N/A'}</span>
                    {chargerFlow.id ? (
                      <button
                        type="button"
                        className={s.idCopy}
                        onClick={() => copyText(chargerFlow.id, 'chargerFlowId')}
                        aria-label="Copy charger flow ID">
                        {copiedKey === 'chargerFlowId' ? (
                          <CheckIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        )}
                      </button>
                    ) : null}
                  </dd>
                </>
              ) : null}
            </dl>

            <PaymentTimeline payment={payment} />

            <CapturePaymentButton payment={payment} />

            <div className={s.primerLinkWrap}>
              <ExternalLinkButton
                href={getProviderDashboardPaymentUrl(payment.provider, payment.providerId ?? '')}
                label={`View in ${providerName}`}
                icon={<OpenInNewIcon sx={{ fontSize: 17 }} />}
                ariaLabel={`View payment in ${providerName} (opens in new tab)`}
              />
              <ExternalLinkButton
                href={getCustomerProfileUrl(customer)}
                label="View Customer in APP"
                icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
                ariaLabel="View customer profile (opens in new tab)"
              />
            </div>
          </div>
        ) : (
          <div className={`${s.tabPanel} ${s.tabPanelRaw}`}>
            <div className={s.rawToolbar}>
              <label className={s.jsonSearchLabel} htmlFor="payload-json-search">
                Search JSON
              </label>
              <div className={s.jsonSearchWrap}>
                <SearchIcon className={s.jsonSearchIcon} sx={{ fontSize: 16 }} aria-hidden />
                <input
                  id="payload-json-search"
                  type="text"
                  className={s.jsonSearchInput}
                  placeholder="Search by path or value (e.g. amount:100)"
                  value={payloadSearch}
                  onChange={(e) => setPayloadSearch(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                {payloadSearch ? (
                  <button
                    type="button"
                    className={s.jsonSearchClear}
                    onClick={() => setPayloadSearch('')}
                    aria-label="Clear JSON search">
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className={`${s.copyBtn} ${copiedKey === 'json' ? s.copyBtnDone : ''}`}
                onClick={() => copyText(json, 'json')}>
                {copiedKey === 'json' ? (
                  <>
                    <CheckIcon sx={{ fontSize: 14 }} /> Copied
                  </>
                ) : (
                  <>
                    <ContentCopyIcon sx={{ fontSize: 14 }} /> Copy JSON
                  </>
                )}
              </button>
            </div>

            {jsonParseFailed ? (
              <p className={s.jsonSearchError} role="status">
                Could not parse payload for path search.
              </p>
            ) : null}
            {parsedSearch.kind !== 'empty' ? (
              <p className={s.jsonSearchMeta} role="status" aria-live="polite">
                {matchSummary}
                {hasMoreMatches ? ` · showing first ${MAX_MATCHES_SHOWN}` : ''}
              </p>
            ) : null}

            {parsedSearch.kind !== 'empty' && visibleMatches.length > 0 ? (
              <ul className={s.matchList} aria-label="Matching JSON paths">
                {visibleMatches.map((entry) => (
                  <li key={entry.path} className={s.matchItem}>
                    <span className={s.matchPath}>{entry.path}</span>
                    <span className={s.matchSep} aria-hidden>
                      {' '}
                      →{' '}
                    </span>
                    <span className={s.matchValue} title={entry.valuePreview}>
                      {entry.valuePreview.length > 120
                        ? `${entry.valuePreview.slice(0, 120)}…`
                        : entry.valuePreview}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className={s.jsonCard}>
              <div className={s.jsonCardActions}>
                <button
                  type="button"
                  className={s.foldBtn}
                  onClick={() => jsonTreeRef.current?.expandAll()}>
                  <UnfoldMoreIcon sx={{ fontSize: 14 }} />
                  Expand all
                </button>
                <button
                  type="button"
                  className={s.foldBtn}
                  onClick={() => jsonTreeRef.current?.collapseAll()}>
                  <UnfoldLessIcon sx={{ fontSize: 14 }} />
                  Collapse all
                </button>
              </div>
              <pre className={s.json}>
                <code>
                  <JsonTree
                    ref={jsonTreeRef}
                    key={payment.id}
                    data={payment}
                    searchHighlight={searchHighlight}
                    matchPaths={pathMatches.map((m) => m.path)}
                  />
                </code>
              </pre>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

export default PaymentDetailDrawer;
